import { useCallback, useEffect, useRef, useState } from 'react';
import * as AttemptModule from 'modules/attempt';
import * as Api from '../api';
import { secondsLeft } from '../constants';
import type { CandidateSection, ExamSnapshot, ExamState } from '../types';

type AnswerValue = unknown;

const SAVE_DEBOUNCE_MS = 700;
/** Codes the API returns when the open section changed under us. */
const STALE_CODES = new Set(['SECTION_TIME_UP', 'SECTION_LOCKED', 'QUESTION_NOT_IN_SECTION']);

interface Options {
  attemptId: string;
  initialAnswers: Map<string, AnswerValue>;
  onFinished: (status: string) => void;
}

export interface ExamSession {
  loading: boolean;
  error: string | null;
  examState: ExamState | null;
  section: CandidateSection | null;
  answers: Map<string, AnswerValue>;
  /** Seconds left in the open section, or in the break. Null when untimed. */
  remaining: number | null;
  onBreak: boolean;
  busy: boolean;
  saving: boolean;
  setAnswer: (questionId: string, value: AnswerValue) => void;
  /** Mirrors an answer the server already stored (Speaking recordings) without saving it again. */
  setLocalAnswer: (questionId: string, value: AnswerValue) => void;
  advance: () => Promise<void>;
  resumeBreak: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Candidate runtime for IELTS/SAT attempts. The server owns deadlines and the
 * open section; this hook mirrors them, saves answers, and moves on when the
 * timer runs out. Every call that changes sections is idempotent server-side,
 * so a double click or two tabs cannot skip a section.
 */
export function useExamSession({ attemptId, initialAnswers, onFinished }: Options): ExamSession {
  const [snapshot, setSnapshot] = useState<ExamSnapshot | null>(null);
  const [answers, setAnswers] = useState<Map<string, AnswerValue>>(initialAnswers);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const skewRef = useRef(0);
  const pendingRef = useRef(new Map<string, AnswerValue>());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finishedRef = useRef(false);
  const autoStepRef = useRef<string | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const apply = useCallback((next: ExamSnapshot) => {
    if (next.examState?.serverNow) {
      skewRef.current = new Date(next.examState.serverNow).getTime() - Date.now();
    }
    setSnapshot(next);
    if (next.finished && !finishedRef.current) {
      finishedRef.current = true;
      onFinishedRef.current(next.status);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await Api.ExamState(attemptId);
      apply(data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load the exam');
    } finally {
      setLoading(false);
    }
  }, [attemptId, apply]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const entries = [...pendingRef.current.entries()];
    if (!entries.length) return;
    pendingRef.current = new Map();
    setSaving(true);
    try {
      await AttemptModule.Api.SaveAnswersBatch(
        attemptId,
        entries.map(([questionId, value]) => ({ questionId, value: value as any })),
      );
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (STALE_CODES.has(code)) {
        // The section closed (deadline or another tab): pick up the new state.
        await refresh();
      } else {
        // Keep the answers queued; the next change or advance retries them.
        for (const [id, v] of entries) if (!pendingRef.current.has(id)) pendingRef.current.set(id, v);
      }
    } finally {
      setSaving(false);
    }
  }, [attemptId, refresh]);

  const setAnswer = useCallback(
    (questionId: string, value: AnswerValue) => {
      setAnswers((prev) => {
        const next = new Map(prev);
        next.set(questionId, value);
        return next;
      });
      pendingRef.current.set(questionId, value);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  const setLocalAnswer = useCallback((questionId: string, value: AnswerValue) => {
    setAnswers((prev) => {
      const next = new Map(prev);
      next.set(questionId, value);
      return next;
    });
  }, []);

  const advance = useCallback(async () => {
    const sectionId = snapshot?.examState?.activeSectionId;
    if (!sectionId || busy) return;
    setBusy(true);
    try {
      await flush();
      const { data } = await Api.AdvanceSection(attemptId, sectionId);
      apply(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not move to the next section');
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [snapshot, busy, flush, attemptId, apply, refresh]);

  const resumeBreak = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await Api.ResumeBreak(attemptId);
      apply(data);
    } catch {
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [busy, attemptId, apply, refresh]);

  // One clock for the whole runner.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Save on tab hide / close so nothing typed is lost.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [flush]);

  const examState = snapshot?.examState ?? null;
  const onBreak = Boolean(examState?.breakUntil && !examState.activeSectionId);
  const activeSection = examState?.sections.find((s) => s.id === examState.activeSectionId);
  const deadline = onBreak ? examState?.breakUntil : activeSection?.deadlineAt;
  const remaining = secondsLeft(deadline, skewRef.current, now);

  // Time is up: close the section (or end the break) once per section.
  useEffect(() => {
    if (remaining !== 0 || busy) return;
    const key = onBreak ? `break:${examState?.breakUntil}` : `section:${examState?.activeSectionId}`;
    if (autoStepRef.current === key) return;
    autoStepRef.current = key;
    if (onBreak) resumeBreak();
    else advance();
  }, [remaining, busy, onBreak, examState, advance, resumeBreak]);

  return {
    loading,
    error,
    examState,
    section: snapshot?.section ?? null,
    answers,
    remaining,
    onBreak,
    busy,
    saving,
    setAnswer,
    setLocalAnswer,
    advance,
    resumeBreak,
    refresh,
  };
}
